import { apiEndpoint } from "../utils/env";

// The APPROVER's API — deliberately separate from adminApi.
//
// Everything here hits /api/Workflow, which is not an admin surface. The people who approve documents are
// ordinary users, and needing admin rights to complete a task assigned to you would either make the feature
// unusable or push customers into handing out admin. Keeping the client split mirrors that boundary, so it is
// obvious at a glance which calls a non-admin can make.

async function fetchWithAuth(url, token, options = {}) {
  const headers = options.headers || {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  headers["Content-Type"] = headers["Content-Type"] || "application/json";

  const res = await fetch(url, { ...options, headers });
  const txt = await res.text();
  let json = null;
  try {
    json = txt ? JSON.parse(txt) : null;
  } catch {
    json = txt;
  }

  if (!res.ok) {
    const msg = json && json.message ? json.message : typeof json === "string" ? json : res.statusText;
    const error = new Error(msg || `Request failed: ${res.status}`);
    // The server's stable code is what a UI should key on — the message is for a person to read.
    error.code = json && json.code ? json.code : null;
    error.status = res.status;
    error.details = json;
    throw error;
  }
  return json;
}

export async function getMyTasks(token, includeCompleted = false) {
  return fetchWithAuth(
    apiEndpoint(`/api/Workflow/MyTasks?includeCompleted=${includeCompleted ? "true" : "false"}`),
    token,
    { method: "GET" }
  );
}

export async function getWorkflowTask(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Workflow/Task/${encodeURIComponent(id)}`), token, { method: "GET" });
}

export async function claimWorkflowTask(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Workflow/Task/${encodeURIComponent(id)}/Claim`), token, { method: "POST" });
}

export async function completeWorkflowTask(token, id, decision, note) {
  return fetchWithAuth(apiEndpoint(`/api/Workflow/Task/${encodeURIComponent(id)}/Complete`), token, {
    method: "POST",
    body: JSON.stringify({ decision, note }),
  });
}

/**
 * Workflow cases opened against one document, for the search detail pane.
 *
 * Lives on the non-admin /api/Workflow surface on purpose: the detail pane is an ordinary user's screen, and
 * the equivalent admin endpoint is role-restricted. Keyed by archive number rather than document id, because
 * that is what a workflow instance actually records as its subject.
 */
export async function getWorkflowForDocument(token, dbName, archiveNo) {
  const qs = `dbName=${encodeURIComponent(dbName)}&archiveNo=${encodeURIComponent(archiveNo)}`;
  return fetchWithAuth(apiEndpoint(`/api/Workflow/ForDocument?${qs}`), token, { method: "GET" });
}

/**
 * The workflow cases this user started themselves, for the home dashboard.
 *
 * Non-admin on purpose, like the rest of this file: the admin instance list is role-restricted, and the home
 * screen is what an ordinary user sees first. The server scopes by StartedBy AND organization, so this shows
 * the caller's own cases only — cases opened by an auto-start rule carry no StartedBy and never appear here.
 */
export async function getMyWorkflowInstances(token, { includeCompleted = true, take = 25 } = {}) {
  const qs = `includeCompleted=${includeCompleted ? "true" : "false"}&take=${encodeURIComponent(take)}`;
  return fetchWithAuth(apiEndpoint(`/api/Workflow/MyInstances?${qs}`), token, { method: "GET" });
}
